import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Calendar, FileText, Plus, Printer, Receipt, Save, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useAtestados, DocumentoEmitido } from "@/hooks/useAtestados";
import { useHonorarios } from "@/hooks/useHonorarios";
import { useClinicaDocumento } from "@/hooks/useClinicaDocumento";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { usePacientes } from "@/hooks/usePacientes";
import { apiCreate, apiDelete, apiEmitReceipt, apiList, apiUpdate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import type { FluxoCaixa } from "@/hooks/useFluxoCaixa";
import { normalizeLocalDate, todayLocalISO } from "@/lib/date-utils";
import { dateBr, printReciboModelo } from "@/lib/documentPrint";
import { queueCommunicationEvent } from "@/lib/campaigns";
import { calculatePaymentFee, isCardPayment } from "@/lib/paymentFees";
import { parseMoneyBR } from "@/lib/money";
import { EnviarDocumentoPaciente } from "@/components/documentos/EnviarDocumentoPaciente";

type ReciboConteudo = {
  servico: string;
  valor: number;
  forma_pagamento: string;
  taxa_operadora_percentual?: number;
  valor_taxa_operadora?: number;
  valor_liquido?: number;
  observacoes?: string;
};

const formasPagamento = ["Pix", "Dinheiro", "Cartão de crédito", "Cartão de débito", "Transferência", "Boleto", "Outro"];
const newReceiptKey = () => globalThis.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

function moneyBr(value: number) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseRecibo(item: DocumentoEmitido): ReciboConteudo {
  try {
    const parsed = JSON.parse(item.conteudo || "{}");
    return {
      servico: parsed.servico || item.titulo || "Serviço odontológico",
      valor: parseMoneyBR(parsed.valor),
      forma_pagamento: parsed.forma_pagamento || "-",
      taxa_operadora_percentual: Number(parsed.taxa_operadora_percentual || 0),
      valor_taxa_operadora: parseMoneyBR(parsed.valor_taxa_operadora),
      valor_liquido: parseMoneyBR(parsed.valor_liquido ?? parsed.valor),
      observacoes: parsed.observacoes || "",
    };
  } catch {
    return {
      servico: item.titulo || "Serviço odontológico",
      valor: 0,
      forma_pagamento: "-",
      observacoes: item.conteudo || "",
    };
  }
}

export function Recibos() {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const { honorarios, fetchHonorarios } = useHonorarios();
  const { informacoes } = useInformacoesClinica();
  const { activeClinicaId, clinicaDocumento } = useClinicaDocumento(informacoes);
  const { documentos, loading, fetchAll } = useAtestados();
  const [selectedPacienteId, setSelectedPacienteId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [movimentacoesCaixa, setMovimentacoesCaixa] = useState<FluxoCaixa[]>([]);
  const savingRef = useRef(false);
  const receiptKeyRef = useRef(newReceiptKey());
  const [formData, setFormData] = useState({
    data_emissao: todayLocalISO(),
    honorario_id: "manual",
    servico: "",
    valor: "",
    forma_pagamento: "Pix",
    taxa_operadora_percentual: "",
    movimentacao_id: "automatico",
    observacoes: "",
  });

  const selectedPaciente = pacientes.find((p) => p.id === selectedPacienteId);
  const dadosClinicaRecibo = useMemo(() => clinicaDocumento(), [clinicaDocumento, informacoes]);
  const recibosPaciente = useMemo(
    () => documentos.filter((item) => item.tipo === "recibo" && item.paciente_id === selectedPacienteId),
    [documentos, selectedPacienteId]
  );

  useEffect(() => {
    if (!dialogOpen) return;
    receiptKeyRef.current = newReceiptKey();
    savingRef.current = false;
    apiList<FluxoCaixa>("fluxo_caixa").then(setMovimentacoesCaixa).catch(() => setMovimentacoesCaixa([]));
  }, [dialogOpen]);

  const entradasCandidatas = useMemo(() => {
    const valor = parseMoneyBR(formData.valor);
    return movimentacoesCaixa.filter((item) => {
      const origemManual = !item.origem || item.origem === "manual";
      const valorCompativel = valor <= 0 || Math.abs(parseMoneyBR(item.valor) - valor) < 0.005;
      return item.tipo === "Entrada"
        && item.status !== "Cancelado"
        && origemManual
        && !item.origem_id
        && normalizeLocalDate(item.data_movimentacao) === formData.data_emissao
        && valorCompativel;
    });
  }, [formData.data_emissao, formData.valor, movimentacoesCaixa]);

  useEffect(() => {
    fetchHonorarios();
  }, []);

  const handleHonorarioChange = (id: string) => {
    if (id === "manual") {
      setFormData((current) => ({ ...current, honorario_id: id, servico: "", valor: "" }));
      return;
    }
    const honorario = honorarios.find((item) => item.id === id);
    setFormData((current) => ({
      ...current,
      honorario_id: id,
      servico: honorario?.procedimento || "",
      valor: String(honorario?.valor_particular || ""),
    }));
  };

  const resetForm = () => {
    setFormData({
      data_emissao: todayLocalISO(),
      honorario_id: "manual",
      servico: "",
      valor: "",
      forma_pagamento: "Pix",
      taxa_operadora_percentual: "",
      movimentacao_id: "automatico",
      observacoes: "",
    });
  };

  const imprimirRecibo = (recibo: DocumentoEmitido) => {
    const conteudo = parseRecibo(recibo);
    printReciboModelo({
      pacienteNome: selectedPaciente?.nome,
      pacienteCpf: selectedPaciente?.cpf,
      data: recibo.data_emissao,
      servico: conteudo.servico,
      valor: conteudo.valor,
      formaPagamento: conteudo.forma_pagamento,
      observacoes: conteudo.observacoes,
      clinica: clinicaDocumento(recibo),
    });
  };

  const handleEmitir = async () => {
    if (savingRef.current) return;
    if (!selectedPacienteId) {
      toast.error("Selecione um paciente para emitir o recibo.");
      return;
    }
    if (!formData.servico.trim()) {
      toast.error("Informe o serviço realizado.");
      return;
    }
    const valor = parseMoneyBR(formData.valor);
    if (!valor || valor <= 0) {
      toast.error("Informe um valor válido para o recibo.");
      return;
    }
    const fee = calculatePaymentFee(valor, formData.forma_pagamento, formData.taxa_operadora_percentual);

    savingRef.current = true;
    setSaving(true);
    let recibo: DocumentoEmitido | null = null;
    try {
      const movimentacoesAtuais = await apiList<FluxoCaixa>("fluxo_caixa");
      const candidatasExatas = movimentacoesAtuais.filter((item) => {
        const origemManual = !item.origem || item.origem === "manual";
        return item.tipo === "Entrada"
          && item.status !== "Cancelado"
          && origemManual
          && !item.origem_id
          && normalizeLocalDate(item.data_movimentacao) === formData.data_emissao
          && Math.abs(parseMoneyBR(item.valor) - valor) < 0.005;
      });
      let entradaExistente: FluxoCaixa | undefined;
      if (formData.movimentacao_id === "automatico") {
        if (candidatasExatas.length > 1) {
          throw new Error("Existem várias entradas com esta data e valor. Selecione o pagamento correto no campo Entrada no caixa.");
        }
        entradaExistente = candidatasExatas[0];
      } else if (formData.movimentacao_id !== "novo") {
        entradaExistente = candidatasExatas.find((item) => item.id === formData.movimentacao_id);
        if (!entradaExistente) throw new Error("A entrada selecionada não está mais disponível ou não corresponde ao valor do recibo.");
      }

      const documentoPayload = {
        paciente_id: selectedPacienteId,
        clinica_id: activeClinicaId,
        clinica_dados: dadosClinicaRecibo,
        tipo: "recibo",
        titulo: `Recibo - ${formData.servico}`,
        conteudo: JSON.stringify({
          servico: formData.servico,
          valor,
          forma_pagamento: formData.forma_pagamento,
          ...fee,
          observacoes: formData.observacoes,
        }),
        profissional: "",
        cro: "",
        data_emissao: formData.data_emissao,
        status: "Emitido",
        idempotency_key: `${receiptKeyRef.current}:documento`,
      };

      const entradaPayload = {
        tipo: "Entrada",
        descricao: `Recibo de ${selectedPaciente?.nome || "paciente"} - ${formData.servico}`,
        categoria: "Recebimento por recibo",
        valor,
        data_movimentacao: formData.data_emissao,
        forma_pagamento: formData.forma_pagamento,
        ...fee,
        observacoes: formData.observacoes,
        origem: "recibo",
        paciente_id: selectedPacienteId,
        paciente_nome: selectedPaciente?.nome || "",
        servico: formData.servico,
        responsavel_nome: user?.nome || user?.email || "Usuário da clínica",
        user_id: user?.id || "",
        idempotency_key: `${receiptKeyRef.current}:caixa`,
      } as const;

      recibo = await apiEmitReceipt<DocumentoEmitido>(documentoPayload, {
        ...entradaPayload,
        observacoes: [entradaExistente?.observacoes, formData.observacoes].filter(Boolean).join(" | "),
        lancamento_manual_original: entradaExistente?.descricao,
      }, entradaExistente?.id);

      await queueCommunicationEvent({
        tipo_evento: "recibo_emitido",
        clinica_id: activeClinicaId,
        paciente_id: selectedPacienteId,
        paciente_nome: selectedPaciente?.nome || "Paciente",
        email: selectedPaciente?.email || "",
        telefone: selectedPaciente?.telefone || "",
        titulo: `${formData.servico} - ${valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`,
        origem_id: recibo.id,
      }).catch(() => undefined);

      await fetchAll();
      printReciboModelo({
        pacienteNome: selectedPaciente?.nome,
        pacienteCpf: selectedPaciente?.cpf,
        data: formData.data_emissao,
        servico: formData.servico,
        valor,
        formaPagamento: formData.forma_pagamento,
        observacoes: formData.observacoes,
        clinica: dadosClinicaRecibo,
      });
      resetForm();
      setDialogOpen(false);
      toast.success("Recibo emitido e recebimento lançado no fluxo de caixa.");
    } catch (error: unknown) {
      if (recibo?.id) {
        const entradaCriada = await apiList<FluxoCaixa>("fluxo_caixa")
          .then((items) => items.some((item) => item.origem === "recibo" && item.origem_id === recibo?.id))
          .catch(() => false);
        if (entradaCriada) {
          await fetchAll();
          resetForm();
          setDialogOpen(false);
          toast.success("Recibo emitido e recebimento confirmado no fluxo de caixa.");
          return;
        }
        await apiDelete("documentos_emitidos", recibo.id).catch(() => undefined);
      }
      await fetchAll();
      toast.error("Não foi possível concluir o recibo: " + (error instanceof Error ? error.message : "erro inesperado"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const [movimentacoes, recibosAtuais] = await Promise.all([
        apiList<FluxoCaixa>("fluxo_caixa"),
        apiList<DocumentoEmitido>("documentos_emitidos"),
      ]);
      const recibo = recibosAtuais.find((item) => item.id === deleteId);
      const entrada = movimentacoes.find((item) => item.origem === "recibo" && item.origem_id === deleteId);

      if (entrada && entrada.status !== "Cancelado") {
        await apiUpdate<FluxoCaixa>("fluxo_caixa", entrada.id, {
          tipo: "Entrada",
          status: "Cancelado",
          cancelado_em: todayLocalISO(),
          cancelado_por: user?.id || "",
          motivo_cancelamento: `Cancelamento do recibo emitido em ${recibo?.data_emissao || "data não informada"}.`,
        });
      }

      await apiUpdate<DocumentoEmitido>("documentos_emitidos", deleteId, { status: "Cancelado" });
      await fetchAll();
      toast.success(entrada ? "Recibo e entrada cancelados no fluxo de caixa." : "Recibo cancelado.");
      setDeleteId(null);
    } catch (error: unknown) {
      toast.error("Não foi possível cancelar o recibo: " + (error instanceof Error ? error.message : "erro inesperado"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Selecionar Paciente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="paciente-recibo">Paciente</Label>
            <Select value={selectedPacienteId} onValueChange={setSelectedPacienteId}>
              <SelectTrigger id="paciente-recibo">
                <SelectValue placeholder="Selecione um paciente" />
              </SelectTrigger>
              <SelectContent>
                {pacientes.map((paciente) => (
                  <SelectItem key={paciente.id} value={paciente.id}>
                    {paciente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPaciente ? (
            <div className="rounded-lg bg-muted p-4 text-sm">
              <p><strong>Email:</strong> {selectedPaciente.email || "-"}</p>
              <p><strong>Telefone:</strong> {selectedPaciente.telefone || "-"}</p>
              <p><strong>CPF:</strong> {selectedPaciente.cpf || "-"}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Para emitir um recibo, selecione o paciente e depois escolha o serviço realizado. Os serviços vêm de Cadastros &gt; Tabela de Preços.
            </p>
          )}
        </CardContent>
      </Card>

      {selectedPacienteId && (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Recibos</h2>
              <p className="text-sm text-muted-foreground">
                Emita recibos vinculados aos serviços realizados para o paciente.
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Recibo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Novo Recibo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input
                        type="date"
                        value={formData.data_emissao}
                        onChange={(event) => setFormData({ ...formData, data_emissao: event.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Forma de pagamento</Label>
                      <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {formasPagamento.map((forma) => (
                            <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {isCardPayment(formData.forma_pagamento) && (
                      <div className="space-y-2 md:col-span-2">
                        <Label>Taxa da operadora (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={formData.taxa_operadora_percentual}
                          onChange={(event) => setFormData({ ...formData, taxa_operadora_percentual: event.target.value })}
                          placeholder="Ex.: 3,49"
                        />
                        <p className="text-xs text-muted-foreground">
                          Taxa {moneyBr(calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual).valor_taxa_operadora)} · Líquido {moneyBr(calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual).valor_liquido)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Serviço realizado</Label>
                    <Select value={formData.honorario_id} onValueChange={handleHonorarioChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o serviço" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Informar manualmente</SelectItem>
                        {honorarios.map((honorario) => (
                          <SelectItem key={honorario.id} value={honorario.id}>
                            {honorario.procedimento} - {moneyBr(honorario.valor_particular)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-4 md:grid-cols-[1fr_180px]">
                    <div className="space-y-2">
                      <Label>Descrição do serviço</Label>
                      <Input
                        value={formData.servico}
                        onChange={(event) => setFormData({ ...formData, servico: event.target.value })}
                        placeholder="Ex.: Restauração, limpeza, avaliação..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Valor recebido</Label>
                      <MoneyInput
                        value={formData.valor}
                        onValueChange={(valor) => setFormData({ ...formData, valor: String(valor) })}
                        placeholder="Ex.: 1.000,00"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Entrada no caixa</Label>
                    <Select value={formData.movimentacao_id} onValueChange={(value) => setFormData({ ...formData, movimentacao_id: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="automatico">Detectar automaticamente</SelectItem>
                        <SelectItem value="novo">Lançar uma nova entrada</SelectItem>
                        {entradasCandidatas.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.descricao} - {moneyBr(parseMoneyBR(item.valor))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Se o pagamento já foi lançado no fluxo de caixa, o recibo reutiliza a entrada existente e não soma novamente.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Textarea
                      value={formData.observacoes}
                      onChange={(event) => setFormData({ ...formData, observacoes: event.target.value })}
                      placeholder="Ex.: referente à parcela 1/3, pagamento integral, desconto aplicado..."
                      rows={3}
                    />
                  </div>

                  <Separator />

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleEmitir} disabled={loading || saving}>
                      <Save className="mr-2 h-4 w-4" />
                      Emitir e Imprimir
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {recibosPaciente.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Receipt className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">
                  Nenhum recibo emitido ainda. Clique em "Novo Recibo" para começar.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Histórico de Recibos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recibosPaciente.map((recibo) => {
                    const conteudo = parseRecibo(recibo);
                    return (
                      <Card key={recibo.id} className="border-l-4 border-l-primary">
                        <CardContent className="p-4">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{dateBr(recibo.data_emissao)}</span>
                                <Badge variant={recibo.status === "Cancelado" ? "destructive" : "secondary"}>{recibo.status === "Emitido" ? "Recibo emitido" : recibo.status}</Badge>
                              </div>
                              <div>
                                <p className="font-medium">{conteudo.servico}</p>
                                <p className="text-sm text-muted-foreground">
                                  {moneyBr(conteudo.valor)} - {conteudo.forma_pagamento}
                                </p>
                                {Number(conteudo.valor_taxa_operadora || 0) > 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    Taxa {moneyBr(conteudo.valor_taxa_operadora || 0)} ({conteudo.taxa_operadora_percentual}%) · Líquido {moneyBr(conteudo.valor_liquido || 0)}
                                  </p>
                                )}
                              </div>
                              {conteudo.observacoes && (
                                <p className="text-sm text-muted-foreground">{conteudo.observacoes}</p>
                              )}
                            </div>
                            <div className="flex justify-end gap-2">
                              <EnviarDocumentoPaciente entidade="documentos_emitidos" documentoId={recibo.id} clinicaId={recibo.clinica_id || activeClinicaId} titulo={recibo.titulo} paciente={selectedPaciente} disabled={recibo.status === "Cancelado"} />
                              <Button variant="outline" size="sm" onClick={() => imprimirRecibo(recibo)}>
                                <Printer className="mr-2 h-4 w-4" />
                                Imprimir
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => setDeleteId(recibo.id)} disabled={recibo.status === "Cancelado" || saving}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              O recibo será cancelado. Se ele possuir entrada financeira, o sistema lançará um estorno no caixa da unidade para preservar o histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
